import { useQuery } from '@zg-rentals/react-query5';
import { ApiError } from '@/src/lib/api/client/apiClient';
import { listing } from '@/src/lib/api/endpoints/listing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { normalizeAmenities } from '@/src/lib/utils/normalizeAmenities';
import { adaptV3ListingDetail } from '@/src/lib/utils/v3ListingDetail';
import { adaptLegacyBuildingDetail, LEGACY_BUILDING_DETAIL_PARAMS } from '@/src/lib/utils/legacyBuildingDetail';
import { listingDetailKeys, normalizeListingDetailUnit } from './queryKeys';

// ============================================================================
// Helpers
// ============================================================================

/**
 * The raw API may return specialOffers at the listing root OR nested inside
 * `details`. The details version contains the full description text while
 * root may only have abbreviated entries — prefer details.
 *
 * Used by both the client-side queryFn and SSR prefetch to ensure the
 * promoted data is always in the React Query cache.
 */
export function promoteSpecialOffers(rawListing: Record<string, unknown>): PropertyDetailListing {
  const rootOffers = rawListing.specialOffers as
    | Array<{ description: string; endDate?: number }>
    | undefined;
  const detailsOffers = (rawListing.details as Record<string, unknown> | undefined)
    ?.specialOffers as Array<{ description: string; endDate?: number }> | undefined;
  const specialOffers = detailsOffers?.length ? detailsOffers : rootOffers;

  const amenities = normalizeAmenities(rawListing.amenities as Record<string, unknown> | undefined);

  return {
    ...rawListing,
    ...(specialOffers?.length ? { specialOffers } : {}),
    ...(amenities ? { amenities } : {}),
  } as unknown as PropertyDetailListing;
}

// ============================================================================
// Hooks
// ============================================================================

export function useListingDetail(
  maloneLotIdEncoded: string | null,
  pdpType: 'unit' | 'building' = 'unit',
  options?: {
    enabled?: boolean;
    initialData?: PropertyDetailListing;
    unit?: string | null;
  },
) {
  const unit = normalizeListingDetailUnit(options?.unit);

  return useQuery<PropertyDetailListing, Error>({
    queryKey: listingDetailKeys.byMaloneLotId(maloneLotIdEncoded ?? '', pdpType, unit),
    initialData: options?.initialData,
    queryFn: async ({ signal }) => {
      if (!maloneLotIdEncoded) {
        throw new Error('Missing listing identifier');
      }

      if (pdpType === 'building') {
        const response = await listing.fetchByMaloneLotIdEncoded(maloneLotIdEncoded, LEGACY_BUILDING_DETAIL_PARAMS);
        const buildingDetail = adaptLegacyBuildingDetail(response, {
          requestedMaloneLotIdEncoded: maloneLotIdEncoded,
        });
        if (!buildingDetail) {
          throw new Error('Missing building detail');
        }
        return buildingDetail.listing;
      }

      const { detail, pricingStats, zillowRegionPricingStats, priceDrops } =
        await listing.fetchListingDetailV3WithPricingStats(maloneLotIdEncoded, {
          unit,
          signal,
        });
      return adaptV3ListingDetail(detail, {
        pdpType,
        requestedMaloneLotIdEncoded: maloneLotIdEncoded,
        selectedUnit: unit,
        pricingStats,
        zillowRegionPricingStats,
        priceDrops,
      });
    },
    enabled: options?.enabled !== false && !!maloneLotIdEncoded,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 1,
    meta: {
      tags: ['listingDetail'],
      context: {
        api: 'hotpads-api',
        details: 'fetch listing detail',
      },
    },
  });
}
